import { createSelector } from 'reselect';
import { overSome, negate } from 'lodash/fp';
import { createPropsExtender } from '@mews/framework';
import { ApplicationState } from '@mews-commander/types';
import { mapBindingToPropSelector } from '@mews-commander/utils';
import { getCurrentProfileId } from '@mews-commander/core';
import {
    getScopeByProfileId,
    getProfileScopeId,
    getScopeEnterpriseId,
    getScopeProfileId,
    getScopeAssignedLearningPath,
    getScopePrivileges,
    hasPlatformScope,
} from '../../ducks/scopes';
import {
    hasCurrentEnterpriseId,
    isScopeAdmin,
    canScopeManagePlatformProfiles,
    canScopeManageReservations,
    canScopeManageTravelAgencies,
    canScopeManageSpaces,
    canScopeRunInstallations,
    canScopeManageContent,
    canScopeConfigureEnterprise,
    canScopeIssueInvoices,
    canScopeReceiveCustomerMessages,
    canScopeManageEnterpriseEmployees,
    canScopeManageCharitySubscriptions,
    canScopeManageSubscriptions,
    canScopeViewSensitiveReports,
    canScopeViewCustomerData,
    canScopeManageServices,
    canScopeManageResourceCategories,
    canScopeManageProducts,
    canScopeManageBusinessSegments,
    canScopeManageRateGroups,
    canScopeManageProductRules,
    canScopeManageRates,
    canScopeManageRestrictions,
    canScopeManageServiceConfigurations,
    canScopeManageAvailability,
    canScopeManageCounters,
    canScopeRebateItems,
    canScopeManageInvoiceability,
    canScopeViewCompanyData,
    canScopeManageCustomersAndCompanies,
    canScopeManageLoyaltyMemberships,
    canScopeManageMailQueue,
    canScopeManageSellLimits,
    canScopeOverrideRatePrice,
    canScopeManagePlatformProfilesWithinEnterprise,
} from './privileges';
import { isCurrentEnterpriseServiceProviderWithBookableService } from './subscription';

// Current profile
const currentProfileIdPropsExtender = createPropsExtender('profileId', () => getCurrentProfileId());
export const [
    getCurrentScopePrivileges,
    getCurrentScope,
    getCurrentScopeProfileId,
    getCurrentScopeAssignedLearningPath,
    getCurrentProfileScopeEnterpriseId,
    getCurrentProfileScopeId,
] = mapBindingToPropSelector(currentProfileIdPropsExtender)([
    getScopePrivileges,
    getScopeByProfileId,
    getScopeProfileId,
    getScopeAssignedLearningPath,
    getScopeEnterpriseId,
    getProfileScopeId,
]);

// Current profile privileges
export const [
    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageSpaces,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageReservations,

    /** @deprecated use {@link usePlatformPrivilege} hook instead */
    canCurrentProfileManagePlatformProfiles,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManagePlatformProfilesWithinEnterprise,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageTravelAgencies,

    /** @deprecated use {@link usePlatformPrivilege} hook instead */
    canCurrentProfileRunInstallations,

    /** @deprecated use {@link usePlatformPrivilege} hook instead */
    canCurrentProfileManageContent,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileConfigureEnterprise,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileIssueInvoices,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageInvoiceability,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileReceiveCustomerMessages,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageEnterpriseEmployees,

    /** @deprecated use {@link usePlatformPrivilege} hook instead */
    canCurrentProfileManageSubscriptions,

    /** @deprecated use {@link usePlatformPrivilege} hook instead */
    canCurrentProfileManageCharitySubscriptions,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileViewSensitiveReports,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileViewCustomerData,

    /** @deprecated use {@link usePrivilege} hook instead */
    isCurrentProfileAdmin,

    /** @deprecated use {@link usePrivilege} hook instead */
    hasCurrentProfilePlatformScope,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageServices,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageResourceCategories,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageProducts,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageBusinessSegments,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageRateGroups,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageProductRules,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageRates,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageRestrictions,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageSellLimits,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageServiceConfigurations,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageAvailability,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageCounters,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileRebateItems,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileViewCompanyData,

    /** @deprecated Use isCurrentProfileEnabledToManageCustomersAndCompanies selector */
    canCurrentProfileManageCustomersAndCompanies,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageLoyaltyMemberships,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileManageMailQueue,

    /** @deprecated use {@link usePrivilege} hook instead */
    canCurrentProfileOverrideRatePrice,
] = mapBindingToPropSelector(currentProfileIdPropsExtender)([
    canScopeManageSpaces,
    canScopeManageReservations,
    canScopeManagePlatformProfiles,
    canScopeManagePlatformProfilesWithinEnterprise,
    canScopeManageTravelAgencies,
    canScopeRunInstallations,
    canScopeManageContent,
    canScopeConfigureEnterprise,
    canScopeIssueInvoices,
    canScopeManageInvoiceability,
    canScopeReceiveCustomerMessages,
    canScopeManageEnterpriseEmployees,
    canScopeManageSubscriptions,
    canScopeManageCharitySubscriptions,
    canScopeViewSensitiveReports,
    canScopeViewCustomerData,
    isScopeAdmin,
    hasPlatformScope,

    canScopeManageServices,
    canScopeManageResourceCategories,
    canScopeManageProducts,
    canScopeManageBusinessSegments,
    canScopeManageRateGroups,
    canScopeManageProductRules,
    canScopeManageRates,
    canScopeManageRestrictions,
    canScopeManageSellLimits,
    canScopeManageServiceConfigurations,
    canScopeManageAvailability,

    canScopeManageCounters,
    canScopeRebateItems,
    canScopeViewCompanyData,
    canScopeManageCustomersAndCompanies,
    canScopeManageLoyaltyMemberships,
    canScopeManageMailQueue,
    canScopeOverrideRatePrice,
]);

export const isCurrentProfilePlatformAdmin = negate(hasCurrentEnterpriseId);

export const hasCurrentProfileAnyServicePrivileges = overSome([
    canCurrentProfileManageServices,
    canCurrentProfileManageResourceCategories,
    canCurrentProfileManageProducts,
    canCurrentProfileManageBusinessSegments,
    canCurrentProfileManageRateGroups,
    canCurrentProfileManageProductRules,
    canCurrentProfileManageRates,
    canCurrentProfileManageRestrictions,
    canCurrentProfileManageServiceConfigurations,
    canCurrentProfileManageAvailability,
]);

export const isCurrentProfileEnabledToManageCompanies = canCurrentProfileManageCustomersAndCompanies;

export const isCurrentProfileEnabledToManageCustomers = canCurrentProfileManageCustomersAndCompanies;

export const hasReadOnlyPrivilegesForCompanyData = createSelector<ApplicationState, boolean, boolean, boolean>(
    canCurrentProfileViewCompanyData,
    isCurrentProfileEnabledToManageCompanies,
    (canViewCompanyData, canManageCompanies) => !canManageCompanies && canViewCompanyData
);

export const hasReadOnlyPrivilegesForCustomerData = createSelector<ApplicationState, boolean, boolean, boolean>(
    isCurrentProfileEnabledToManageCustomers,
    canCurrentProfileViewCustomerData,
    (canManageCustomers, canViewCustomerData) => !canManageCustomers && canViewCustomerData
);

export const canCurrentEmployeeUseTimeline = createSelector(
    isCurrentProfilePlatformAdmin,
    isCurrentEnterpriseServiceProviderWithBookableService,
    isCurrentProfileEnabledToManageCustomers,
    (isPlatformAdmin, isServiceProvider, canManageCustomers) =>
        !isPlatformAdmin && isServiceProvider && canManageCustomers
);

export const canCurrentEmployeeManageReservations = createSelector(
    isCurrentProfilePlatformAdmin,
    canCurrentProfileManageReservations,
    isCurrentEnterpriseServiceProviderWithBookableService,
    (isPlatformAdmin, canManageReservation, isServiceProvider) =>
        !isPlatformAdmin && canManageReservation && isServiceProvider
);
